import React from 'react';
import Zana from './Zana';
import './ZanaModeUpsell.css';

/* ============================================================================
   ZanaModeUpsell — the "Zana Mode trailer" shown to Basic & Pilot orgs in place
   of the real Zana Mode tab. A dozing Zana, a preview of everything Zana does
   once she's awake, and an "Upgrade to wake Zana" CTA. Self-contained styles so
   it renders correctly both inside BusAnalyticsPage (Basic) and standalone in
   BusTracking (Pilot). Premium/Enterprise orgs never see this — they get the
   live ZanaModeTab instead.
   ========================================================================== */

interface Feat {
  d: string;         // svg path
  title: string;
  body: string;
}

const FEATS: Feat[] = [
  { d: 'M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v6h1a2 2 0 0 1 2 2v4a1.5 1.5 0 0 0 3 0V9.8l-2-2 1-1 2.3 2.3c.4.4.7 1 .7 1.6V17a3.5 3.5 0 0 1-7 0v-4h-1v8H5Zm2-12h6V5H7v4Z',
    title: 'Fleet Health Briefing', body: 'A weekly owner briefing: headline, health score and the two things to fix now.' },
  { d: 'M12 2 9.2 8.8 2 9.5l5.4 4.7L5.8 22 12 18l6.2 4-1.6-7.8L22 9.5l-7.2-.7L12 2Z',
    title: 'Fuel Anomaly & Theft', body: 'Each vehicle judged against its own history — catches siphoning and drift, not false alarms.' },
  { d: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 10V6h-2v7h6v-2h-4Z',
    title: 'Smart ETA Predictions', body: 'Learns each route & driver’s real pace to predict honest arrival windows.' },
  { d: 'M12 2 4 5v6c0 5 3.4 8.7 8 11 4.6-2.3 8-6 8-11V5l-8-3Z',
    title: 'Driver Coaching', body: 'Performance scores with one specific coaching point per driver, per week.' },
  { d: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm8.9 4 1.8 1.4-1.8 3.1-2.2-.7a7 7 0 0 1-1.4.8l-.4 2.3H10.1l-.4-2.3a7 7 0 0 1-1.4-.8l-2.2.7L4.3 13.4 6.1 12l-1.8-1.4 1.8-3.1 2.2.7 1.4-.8.4-2.3h3.8l.4 2.3 1.4.8 2.2-.7 1.8 3.1L20.9 12Z',
    title: 'Predictive Maintenance', body: 'Flags vehicles trending toward a breakdown so you service before the road does.' },
  { d: 'M13 2 3 14h7l-1 8 10-12h-7l1-8Z',
    title: 'Route & Cost Savings', body: 'Ranked, specific recommendations to cut spend and lift fleet-wide efficiency.' },
];

export default function ZanaModeUpsell({ onUpgrade, plan }: { onUpgrade?: () => void; plan?: string }) {
  const isPilot = (plan || '').toLowerCase() === 'pilot' || !plan;
  return (
    <div className="zana-up">
      {/* ── Trailer hero ─────────────────────────────────────────── */}
      <div className="zana-up-hero">
        <div className="zana-up-glow" />
        <div className="zana-up-robot"><Zana state="sleeping" size={132} ground animated /></div>
        <div className="zana-up-body">
          <div className="zana-up-eyebrow"><span className="zana-up-badge">ZANA MODE</span> Premium &amp; Enterprise</div>
          <h3 className="zana-up-headline">Zana is asleep on your {isPilot ? 'pilot' : 'plan'}.</h3>
          <p className="zana-up-summary">
            Wake Zana — your AI fleet copilot. She reads every trip, tank and route and turns fuel theft,
            wasted routes, driver habits and looming breakdowns into plain-English decisions, quietly in the background.
          </p>
          <button className="zana-up-cta" onClick={() => onUpgrade?.()}>
            Upgrade to wake Zana
            <svg viewBox="0 0 24 24" width="16" height="16" style={{ fill: 'currentColor' }}><path d="M5 11h11.2l-4.6-4.6L13 5l7 7-7 7-1.4-1.4 4.6-4.6H5v-2Z" /></svg>
          </button>
          <div className="zana-up-note">Included on Premium &amp; Enterprise · no metering, no AI credits · runs on the data the driver’s phone already collects.</div>
        </div>
      </div>

      {/* ── What Zana does once awake ─────────────────────────────── */}
      <div className="zana-up-featgrid">
        {FEATS.map((f) => (
          <div key={f.title} className="zana-up-feat">
            <span className="zana-up-ic"><svg viewBox="0 0 24 24" width="22" height="22" style={{ fill: '#fff' }}><path d={f.d} /></svg></span>
            <div className="zana-up-feat-title">{f.title}</div>
            <p className="zana-up-feat-body">{f.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
